import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { delay, filter, first, map, of, switchMap, withLatestFrom } from 'rxjs';

import { BreakDialogComponent } from 'app/features/manual-input-registration/components';
import { Break, RegistrationType } from 'app/features/manual-input-registration/models';
import { selectScanFlowRegistrationDataCompleteness } from 'app/features/scan-flow/state/scan-flow/scan-flow-labor-registration';
import { sendCurrentScanFlowRegistrationData } from 'app/features/scan-flow/state/scan-flow/scan-flow-labor-registration/scan-flow-labor-registration.actions';
import { selectAppSettingsIfDefined } from 'app/features/settings/state/settings/settings.selectors';
import { breakStorageKey, StorageService } from 'app/shared/services';
import { selectActivityByNumber } from 'app/state/activities/activity.selectors';
import { closeDialog, openDialog } from 'app/state/app-state/app-state.actions';
import { addTimeRegistration } from 'app/state/registrations/registrations.actions';
import { userSessionClosed } from 'app/state/user-session/user-session.actions';
import { selectUserSessionEmployeeNumber } from 'app/state/user-session/user-session.selectors';

import {
    applyingBreakDataFromStorage,
    breakCancelled,
    breakEnded,
    breakEnding,
    breakStarted,
    breakStarting,
    initializingBreakStore,
} from './break.actions';
import { selectBreakDialogContext, selectBreakIfLoaded } from './break.selectors';

// noinspection JSUnusedGlobalSymbols
@Injectable()
export class BreakEffects {
    startBreakTimeRegistration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(breakStarted),
            map(({ breakData }) =>
                addTimeRegistration({
                    activityNumber: undefined,
                    employeeNumber: breakData.employeeNumber,
                    registrationTimestamp: breakData.startTimestamp,
                    registrationType: RegistrationType.StartPause,
                }),
            ),
        ),
    );

    endBreakTimeRegistration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(breakEnded),
            map(({ breakData, endTimestamp }) =>
                addTimeRegistration({
                    activityNumber: breakData.activityNumber,
                    employeeNumber: breakData.employeeNumber,
                    registrationTimestamp: endTimestamp,
                    registrationType: RegistrationType.EndPause,
                }),
            ),
        ),
    );

    sendCompleteScanFlowRegistrationBeforeBreakStarted$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(breakStarted),
            withLatestFrom(this.store.pipe(selectScanFlowRegistrationDataCompleteness)),
            filter(([, completeness]) => completeness === 'complete'),
            map(([{ breakData }]) =>
                sendCurrentScanFlowRegistrationData({ registrationTimestamp: breakData.startTimestamp }),
            ),
        );
    });

    storeBreakDataInStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(initializingBreakStore),
                switchMap(() => this.store.pipe(selectBreakIfLoaded)),
                map((breakData) => {
                    this.storageService.store(breakStorageKey, breakData);
                    return true;
                }),
            ),
        { dispatch: false },
    );

    startBreak$ = createEffect(() =>
        this.actions$.pipe(
            ofType(breakStarting),
            withLatestFrom(this.store.select(selectUserSessionEmployeeNumber)),
            filter(([, employeeNumber]) => !!employeeNumber),
            map(([{ activity }, employeeNumber]) =>
                breakStarted({
                    breakData: {
                        activityNumber: activity.number,
                        employeeNumber: employeeNumber ?? 0,
                        startTimestamp: Date.now(),
                    },
                }),
            ),
        ),
    );

    endBreak$ = createEffect(() =>
        this.actions$.pipe(
            ofType(breakEnding),
            withLatestFrom(this.store.pipe(selectBreakIfLoaded)),
            map(([{ endTimestamp }, breakData]) => ({ breakData, endTimestamp })),
            filter((input): input is { breakData: Break; endTimestamp: number } => !!input.breakData),
            map(({ breakData, endTimestamp }) => breakEnded({ breakData, endTimestamp })),
        ),
    );

    openCloseBreakDialog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initializingBreakStore),
            switchMap(() => this.store.select(selectBreakDialogContext)),
            switchMap((dialogContext) =>
                dialogContext
                    ? this.store.pipe(selectActivityByNumber(dialogContext.activityNumber), first())
                    : of(undefined),
            ),
            map((activity) =>
                activity
                    ? openDialog({
                          dialog: {
                              component: BreakDialogComponent,
                              inputs: { activity },
                          },
                      })
                    : closeDialog(),
            ),
            // delay added to force change detection
            delay(1),
        ),
    );

    loadingBreakDataFromStorage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initializingBreakStore),
            map(() => {
                const breakDataFromStorage = this.storageService.get<Break>(breakStorageKey);
                return applyingBreakDataFromStorage({ breakDataFromStorage });
            }),
        ),
    );

    endBreakOnUserSessionClosedInPersonalMode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userSessionClosed),
            withLatestFrom(this.store.pipe(selectAppSettingsIfDefined), this.store.pipe(selectBreakIfLoaded)),
            filter(([, appSettings, breakData]) => appSettings.appMode === 'personal' && !!breakData),
            map(([{ timestamp, sessionExpired }]) =>
                sessionExpired ? breakCancelled() : breakEnding({ endTimestamp: timestamp }),
            ),
        ),
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private storageService: StorageService,
    ) {}
}
